import "./style.scss";

import * as Yup from "yup";

import { FC, useContext, useState, useRef } from "react";
import { Field, Form, Formik, FormikProps } from "formik";

import Alert from "shared/ui/Alert";
import Button from "shared/ui/Button";
import Input from "shared/ui/Input";
import Loader from "shared/ui/Loader";
import Select from "shared/ui/Select";
import { UserContext } from "features/account";
import VerifyDatabaseDocumentResult from "./VerifyDatabaseDocumentResult";
import { useGetDarkWebDocument } from "features/analysis";
import { useSearchLostDocument } from "features/lost";
import SelectCountryEU from "shared/ui/SelectCountryEU";
import TrustfullVerificationForm, { TrustfullFormValues } from "../TrustfullVerificationForm/TrustfullVerificationForm";
import { documentNumberSchema } from "../../utils/documentEU_regex";
import { TrustfullResponse } from "shared/types/types";

interface FormValues {
    documentNumber: string;
    documentType: string;
    documentCountry: string;
}

const options = [
    { value: "passport", label: "Passeport" },
    { value: "id_card", label: "Carte d'identité" },
    { value: "residency_permit", label: "Carte de séjour" },
    { value: "driver_permit", label: "Permis de conduire" },
];

type VerifyDatabaseDocumentProps = {
    type: string;
    setType: (type: string) => void;
    setVerificationDone: React.Dispatch<React.SetStateAction<boolean>>;
    verificationDone: boolean;
    hideHeader?: boolean;
    hideBtn?: boolean;
    setDocNumber?: (docNumber: string) => void;
    docNumber?: string;
    setDocCountryCode?: (code: string) => void;
    docCountryCode?: string;
    showTrusfullVerification?: boolean;
    hideBirthFields?: boolean;
};

const VerifyDatabaseDocument: FC<VerifyDatabaseDocumentProps> = ({
                                                                     type,
                                                                     setType,
                                                                     setVerificationDone,
                                                                     hideHeader = false,
                                                                     hideBtn = false,
                                                                     setDocNumber,
                                                                     setDocCountryCode,
                                                                     docNumber = "",
                                                                     docCountryCode = "",
                                                                     showTrusfullVerification=true,
                                                                     hideBirthFields=false
                                                                 }) => {
    const [result, setResult] = useState<any>();
    const [trustfullData, setTrustfullData] = useState<TrustfullResponse | undefined>(undefined);
    const [isTrustfullLoading, setIsTrustfullLoading] = useState(false);
    const [resetTrustfullForm, setResetTrustfullForm] = useState(false);
    const { mutate: searchDocument, isLoading: isDocumentLoading } = useSearchLostDocument();
    const { userType, user } = useContext(UserContext);
    const { mutate: getDarkWebDocument } = useGetDarkWebDocument();
    const [countryCode, setCountryCode] = useState('FRA');


    // Refs for forms
    const documentFormikRef = useRef<FormikProps<FormValues>>(null);
    const trustfullSubmitRef = useRef<(() => void) | null>(null);

    const handleCheckDarkWeb = (number: string, typeDocument: string, pays: string) => {
        const data: any = {
            number,
            typeDocument,
            pays,
            fromCompanyName: user?.companyName
        };

        getDarkWebDocument(data, {
            onSuccess: (response) => {
                setResult((prev: any) => ({ ...prev, darkWeb: response }));
            },
            onError: (error) => {
                setResult((prev: any) => ({ ...prev, darkWeb: false }));
            },
        });
    };

    const handleDocumentSubmit = async (values: FormValues) => {
        handleCheckDarkWeb(values.documentNumber.toUpperCase(), values.documentType, values.documentCountry);

        if (userType) {
            const searchParams: any = {
                type: userType,
                docType: values.documentType,
                idNumber: values.documentNumber.toUpperCase(),
            };

            searchDocument(searchParams, {
                onSuccess: (response) => {
                    setResult((prev: any) => ({
                        ...prev,
                        status: response.status,
                        numeroDocument: values.documentNumber.toUpperCase(),
                        country: values.documentCountry,
                        typeDocument: values.documentType,
                    }));

                    if (response.status === "fraude" || response.status === "error") {
                        setType("warning");
                    }
                },
                onError: (error) => {
                    setResult((prev: any) => ({
                        ...prev,
                        status: 'not_found',
                        numeroDocument: values.documentNumber.toUpperCase(),
                        country: values.documentCountry,
                        typeDocument: values.documentType,
                    }));
                },
            });
        }
    };

    const handleMainSubmit = async () => {
        console.log("Main submit clicked");

        // Submit document form first (don't wait for it)
        if (documentFormikRef.current) {
            console.log("Submitting document form");
            documentFormikRef.current.submitForm();
        }

        // Immediately submit Trustfull form regardless of document verification
        if (trustfullSubmitRef.current) {
            console.log("Submitting  form immediately");
            trustfullSubmitRef.current();
        } else {
            console.log(" submit not available yet");
        }

        setVerificationDone(true);
    };

    const refresh = () => {
        setResult(undefined);
        setTrustfullData(undefined);
        setResetTrustfullForm(true);
        setVerificationDone(false);

        // Reset the reset flag after a tick
        setTimeout(() => setResetTrustfullForm(false), 100);
    };

    const handleTrustfullVerificationComplete = (data: TrustfullResponse) => {
        setTrustfullData(data);
        setIsTrustfullLoading(false);
    };

    const handleTrustfullVerificationError = (error: string) => {
        console.error(" verification error:", error);
        setIsTrustfullLoading(false);
    };

    const handleTrustfullRefresh = () => {
        setTrustfullData(undefined);
        setResetTrustfullForm(true);
        setTimeout(() => setResetTrustfullForm(false), 100);
    };

    const registerTrustfullSubmit = (submitFn: () => void) => {
        console.log("Registering  submit function");
        trustfullSubmitRef.current = submitFn;
    };

    const isLoading = isDocumentLoading || isTrustfullLoading;

    if (isLoading) {
        return <Loader />;
    }

    // Check if document form is valid
    const isDocumentFormValid = documentFormikRef.current?.isValid || false;
    const isDocumentFormFilled = documentFormikRef.current?.values?.documentNumber &&
      documentFormikRef.current?.values?.documentType;
    const isMainButtonDisabled = !isDocumentFormValid || !isDocumentFormFilled || isLoading;

    return (
      <div className="VerifyDatabaseDocument d-flex flex-column">
          {!hideHeader && (
            <h1 className="VerifyDatabaseDocument__title h3">
                {!result ? "Vérifier un document" : result?.status === "not_found" ? "Document inconnu" : "Document reconnu"}
            </h1>
          )}

          {(!result && !trustfullData) ? (
            <>
                <Formik
                  innerRef={documentFormikRef}
                  initialValues={{ documentNumber: docNumber, documentType: type, documentCountry: docCountryCode === "" ? countryCode : docCountryCode }}
                  validationSchema={Yup.object().shape({
                      documentNumber: documentNumberSchema(type, countryCode),
                      documentType: Yup.string().required("Ce champ est requis"),
                      documentCountry: Yup.string().required("Ce champ est requis"),
                  })}
                  onSubmit={handleDocumentSubmit}>
                    {({ values, handleChange, errors, isValid }: any) => (
                      <Form>
                          <div className="d-flex flex-column align-items-end justify-content-between h-100">
                              <div className="w-100">
                                  <div className="d-flex gap-1 mb-3">
                                      <Field
                                        name="documentCountry"
                                        value={values.documentCountry}
                                        id="documentCountry"
                                        onChange={handleChange}
                                      >
                                          {({ field, country }: any) => (
                                            <SelectCountryEU
                                              field={field}
                                              country={country}
                                              handleChange={(value: string) => {
                                                  setCountryCode(value);
                                                  if (setDocCountryCode) {
                                                      setDocCountryCode(value);
                                                  }
                                              }}
                                            />
                                          )}
                                      </Field>
                                      <Field
                                        name="documentType"
                                        onChange={handleChange}
                                        value={values.documentType}
                                      >
                                          {({ field }: any) => (
                                            <Select
                                              className="w-50"
                                              field={field}
                                              placeholder="Type de document"
                                              options={options}
                                              handleChange={(value) => {
                                                  setType(value);
                                              }}
                                              errorMessage={!isValid ? errors.documentType : ""}
                                            />
                                          )}
                                      </Field>
                                  </div>
                                  <Input
                                    type="text"
                                    name="documentNumber"
                                    placeholder="n° de document"
                                    required
                                    onChange={(value) => {
                                        if (setDocNumber) {
                                            setDocNumber(value.target.value);
                                        }
                                        return handleChange(value);
                                    }}
                                    value={values.documentNumber}
                                    className="mb-1"
                                    errorMessage={!isValid ? errors.documentNumber : ""}
                                  />

                                  {/* Trustfull Verification Form - Always enabled */}
                                  {showTrusfullVerification && (
                                    <TrustfullVerificationForm
                                      disabled={isLoading}
                                      onVerificationComplete={handleTrustfullVerificationComplete}
                                      onVerificationError={handleTrustfullVerificationError}
                                      onSubmitTrustfull={registerTrustfullSubmit}
                                      resetForm={resetTrustfullForm}
                                      setLoading={setIsTrustfullLoading} // Add this prop
                                      hideBirthFields={hideBirthFields}
                                    />
                                  )}

                                  {!hideHeader && values.documentNumber && <Alert type="info">ID Protect ne conserve aucune données utilisateur</Alert>}
                              </div>
                              {!hideBtn && (
                                <Button
                                  className="btn btn-primary"
                                  label="Vérifier"
                                  onClick={handleMainSubmit}
                                  // disabled={isMainButtonDisabled}
                                />
                              )}
                          </div>
                      </Form>
                    )}
                </Formik>
            </>
          ) : (
            <VerifyDatabaseDocumentResult
              result={result}
              trustfullData={trustfullData}
              refresh={refresh}
              onRefreshTrustfull={handleTrustfullRefresh}
              isTrustfullLoading={isTrustfullLoading}
              showTrusfullVerification={showTrusfullVerification}
            />
          )}
      </div>
    );
};

export default VerifyDatabaseDocument;